import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import {
  AddIcon,
  Button,
  FlexContent,
  Form,
  FormField,
  Input,
  ModalDialog,
  Numeric,
  Select,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import Table from "../../../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminLockModule, AdminLockModuleCoefficient, LockModuleCalculationCase } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { PaginationQuery } from "../../../../types";

const CALCULATION_CASES: LockModuleCalculationCase[] = ["NOTCH", "INCLINED", "LOCKSTOP"];

interface FormBody {
  code: string;
  assemblyHeight: number | null;
  sideHeight: number | null;
  profileThickness: number | null;
  moduleWidth: number | null;
  joistFastenersPerModule: number | null;
  headFastenersPerModule: number | null;
  kLockFactor: number | null;
  f1AluRk: number | null;
  f2AluRkPerModule: number | null;
  kh2PerModule: number | null;
  contactAreaALock: number | null;
  cMin: number | null;
}

const AdminLockModulesTab: FC = () => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "lockModules" });
  const aborter = useRef(new AbortController());
  const debouncedValue = filters.state.q;
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = adminApi.useLockModules(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const [itemToDelete, setItemToDelete] = useState<AdminLockModule>();
  const [itemToEdit, setItemToEdit] = useState<AdminLockModule>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminLockModule>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}><Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.common.code}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockModules.assemblyHeight}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockModules.sideHeight}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockModules.thickness}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockModules.moduleWidth}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockModules.joistFastenersPerModule}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockModules.headFastenersPerModule}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.lockModules.cMin}</TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{item.code}</TableCell>
              <TableCell>{item.assemblyHeight}</TableCell>
              <TableCell>{item.sideHeight ?? i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
              <TableCell>{item.profileThickness ?? i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
              <TableCell>{item.moduleWidth}</TableCell>
              <TableCell>{item.joistFastenersPerModule ?? i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
              <TableCell>{item.headFastenersPerModule ?? i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
              <TableCell>{item.cMin ?? i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.lockCalculatorModule.admin.lockModules.singular}
            isSubmitting={isSubmitting}
            buttons={[<Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteLockModule(itemToDelete.id)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}>{i18n.translation.common.yes}</Button>]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <FormModal onClose={() => setIsCreateOpen(false)} />}
      {!!itemToEdit && <FormModal item={itemToEdit} onClose={() => setItemToEdit(undefined)} />}
    </>
  );
};

/** Inline CRUD for a lock_module's calculation_case coefficients (nef/klat
 * per calculation_case + n_modules — lock_calculator.lock_module_coefficients).
 * Only shown once the module has an id (i.e. when editing, not creating). */
const CoefficientsEditor: FC<{ lockModuleId: string }> = ({ lockModuleId }) => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const aborter = useRef(new AbortController());
  const { data, error } = adminApi.useLockModuleCoefficients(lockModuleId, aborter.current.signal);

  const [draft, setDraft] = useState<{
    calculationCase: LockModuleCalculationCase;
    nModules: number | null;
    nef: number | null;
    klat: number | null;
  }>({ calculationCase: "NOTCH", nModules: 1, nef: null, klat: null });

  const addRow = async () => {
    if (!draft.nModules) return;
    try {
      await adminApi.insertLockModuleCoefficient(lockModuleId, {
        calculationCase: draft.calculationCase,
        nModules: draft.nModules,
        nef: draft.nef,
        klat: draft.klat,
      });
      setDraft({ ...draft, nef: null, klat: null });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateRow = async (row: AdminLockModuleCoefficient, patch: Partial<AdminLockModuleCoefficient>) => {
    try {
      await adminApi.updateLockModuleCoefficient(lockModuleId, row.id, patch);
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const deleteRow = async (row: AdminLockModuleCoefficient) => {
    try {
      await adminApi.deleteLockModuleCoefficient(lockModuleId, row.id);
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <FormField label={i18n.translation.lockCalculatorModule.admin.lockModules.coefficients} isOptional>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.5em" }}>
        {error && <div>{i18n.translation.common.error}</div>}
        {(data?.items ?? []).map((row) => (
          <div key={row.id} style={{ display: "flex", gap: "0.5em", alignItems: "center" }}>
            <Select<{ value: LockModuleCalculationCase; label: string }, false>
              options={CALCULATION_CASES.map((c) => ({ value: c, label: c }))}
              isMulti={false}
              getOptionLabel={(x) => x.label}
              getOptionValue={(x) => x.value}
              value={{ value: row.calculationCase, label: row.calculationCase }}
              onChange={(option) => option && updateRow(row, { calculationCase: option.value })}
            />
            <Numeric
              value={row.nModules}
              decimalScale={0}
              placeholder="n_modules"
              onValueChange={(v) => v != null && updateRow(row, { nModules: v })}
            />
            <Numeric
              value={row.nef ?? undefined}
              decimalScale={3}
              placeholder="nef"
              onValueChange={(v) => updateRow(row, { nef: v ?? null })}
            />
            <Numeric
              value={row.klat ?? undefined}
              decimalScale={3}
              placeholder="klat"
              onValueChange={(v) => updateRow(row, { klat: v ?? null })}
            />
            <Button variant="secondary" onClick={() => deleteRow(row)}>
              {i18n.translation.common.delete}
            </Button>
          </div>
        ))}
        <div style={{ display: "flex", gap: "0.5em", alignItems: "center" }}>
          <Select<{ value: LockModuleCalculationCase; label: string }, false>
            options={CALCULATION_CASES.map((c) => ({ value: c, label: c }))}
            isMulti={false}
            getOptionLabel={(x) => x.label}
            getOptionValue={(x) => x.value}
            value={{ value: draft.calculationCase, label: draft.calculationCase }}
            onChange={(option) => option && setDraft({ ...draft, calculationCase: option.value })}
          />
          <Numeric
            value={draft.nModules ?? undefined}
            decimalScale={0}
            placeholder="n_modules"
            onValueChange={(v) => setDraft({ ...draft, nModules: v ?? null })}
          />
          <Numeric
            value={draft.nef ?? undefined}
            decimalScale={3}
            placeholder="nef"
            onValueChange={(v) => setDraft({ ...draft, nef: v ?? null })}
          />
          <Numeric
            value={draft.klat ?? undefined}
            decimalScale={3}
            placeholder="klat"
            onValueChange={(v) => setDraft({ ...draft, klat: v ?? null })}
          />
          <Button onClick={addRow}>{i18n.translation.common.add}</Button>
        </div>
      </div>
    </FormField>
  );
};

const FormModal: FC<{ item?: AdminLockModule; onClose: () => void }> = ({ item, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();

  const defaultValues: FormBody = {
    code: item?.code ?? "",
    assemblyHeight: item?.assemblyHeight ?? null,
    sideHeight: item?.sideHeight ?? null,
    profileThickness: item?.profileThickness ?? null,
    moduleWidth: item?.moduleWidth ?? null,
    joistFastenersPerModule: item?.joistFastenersPerModule ?? null,
    headFastenersPerModule: item?.headFastenersPerModule ?? null,
    kLockFactor: item?.kLockFactor ?? null,
    f1AluRk: item?.f1AluRk ?? null,
    f2AluRkPerModule: item?.f2AluRkPerModule ?? null,
    kh2PerModule: item?.kh2PerModule ?? null,
    contactAreaALock: item?.contactAreaALock ?? null,
    cMin: item?.cMin ?? null,
  };

  const onSubmit = () => async (formData: FormBody) => {
    try {
      if (item) {
        await adminApi.updateLockModule(item.id, {
          code: formData.code,
          assemblyHeight: formData.assemblyHeight ?? undefined,
          sideHeight: formData.sideHeight,
          profileThickness: formData.profileThickness,
          moduleWidth: formData.moduleWidth ?? undefined,
          joistFastenersPerModule: formData.joistFastenersPerModule,
          headFastenersPerModule: formData.headFastenersPerModule,
          kLockFactor: formData.kLockFactor ?? undefined,
          f1AluRk: formData.f1AluRk ?? undefined,
          f2AluRkPerModule: formData.f2AluRkPerModule ?? undefined,
          kh2PerModule: formData.kh2PerModule,
          contactAreaALock: formData.contactAreaALock ?? undefined,
          cMin: formData.cMin,
        });
      } else {
        await adminApi.insertLockModule({
          code: formData.code,
          assemblyHeight: formData.assemblyHeight ?? 0,
          sideHeight: formData.sideHeight,
          profileThickness: formData.profileThickness,
          moduleWidth: formData.moduleWidth ?? 0,
          joistFastenersPerModule: formData.joistFastenersPerModule,
          headFastenersPerModule: formData.headFastenersPerModule,
          kLockFactor: formData.kLockFactor ?? 0,
          f1AluRk: formData.f1AluRk ?? 0,
          f2AluRkPerModule: formData.f2AluRkPerModule ?? 0,
          kh2PerModule: formData.kh2PerModule,
          contactAreaALock: formData.contactAreaALock ?? 0,
          cMin: formData.cMin,
        });
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={
        item
          ? `${i18n.translation.common.edit} ${i18n.translation.lockCalculatorModule.admin.lockModules.singular}`
          : `${i18n.translation.common.add} ${i18n.translation.lockCalculatorModule.admin.lockModules.singular}`
      }
    >
      <Form<FormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
       
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            {!item && (
              <Controller
                name="code"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.translation.common.code} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Input id={labelId} value={field.value} isInvalid={isInvalid} onChange={field.onChange} />
                    )}
                  </FormField>
                )}
              />
            )}
            <Controller
              name="assemblyHeight"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockModules.assemblyHeight}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={1}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="sideHeight"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockModules.sideHeight}
                  error={fieldState.error}
                  isOptional
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={1}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="profileThickness"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockModules.thickness}
                  error={fieldState.error}
                  isOptional
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={1}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="moduleWidth"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockModules.moduleWidth}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={1}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="joistFastenersPerModule"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockModules.joistFastenersPerModule}
                  error={fieldState.error}
                  isOptional
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="headFastenersPerModule"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockModules.headFastenersPerModule}
                  error={fieldState.error}
                  isOptional
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={0}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="kLockFactor"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockModules.kLockFactor}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={3}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="f1AluRk"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockModules.f1AluRk}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={2}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="f2AluRkPerModule"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockModules.f2AluRkPerModule}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={2}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="kh2PerModule"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockModules.kh2PerModule}
                  error={fieldState.error}
                  isOptional
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={3}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="contactAreaALock"
              control={control}
              rules={{ required: true, min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockModules.contactAreaALock}
                  error={fieldState.error}
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={1}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="cMin"
              control={control}
              rules={{ min: 0 }}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.lockCalculatorModule.admin.lockModules.cMin}
                  error={fieldState.error}
                  isOptional
                 
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      id={labelId}
                      value={field.value ?? undefined}
                      decimalScale={1}
                      isInvalid={isInvalid}
                      onValueChange={(value) => field.onChange(value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
      {item && <CoefficientsEditor lockModuleId={item.id} />}
    </ModalDialog>
  );
};

export default AdminLockModulesTab;
